import { ReactNode, useEffect, useState } from 'react'

import { clsx } from 'clsx'
import {
  ControlledTreeEnvironment,
  InteractionMode,
  TreeItemIndex,
} from 'react-complex-tree'
import { useParams } from 'react-router'

import { Slate } from '@tribeplatform/gql-client/types'
import { useAuthMember, useNetwork } from '@tribeplatform/react-sdk/hooks'
import { useSlateKitContext } from '@tribeplatform/react-slate-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useQuery } from '../../../../hooks/useQuery.js'
import {
  useCustomizer,
  useCustomizerBlockSettings,
  useCustomizerNavigation,
  useCustomizerSpace,
} from '../hooks/index.js'
import { CustomizerActionType, ExtendedSlateProps } from '../types.js'
import {
  getExtendedSlatesProps,
  getSlateTreeActions,
  getSlateTreeItems,
} from '../utils.js'
import { BlockEditor } from './BlockEditor.js'
import { renderDepthOffset } from './constants.js'
import { DisableNavigationPicker } from './DisableNavigationPicker.js'
import { LayoutPicker } from './LayoutPicker.js'
import { SlateEditorContext } from './slate-editor.hook.js'
import { SlateTreeItem } from './SlateTreeItem.js'
import { SlateTreeItemInfo } from './types.js'

export type SlateEditorProps = {
  slates: { id: string; slate: Slate }[]
  children?: ReactNode
}

export const SlateEditor = ({ slates, children }: SlateEditorProps) => {
  const { state, dispatch } = useCustomizer()
  const { selectedBlock } = state
  const { path, spaceId, space, layout } = useCustomizerSpace()
  const { editNavigation } = useCustomizerNavigation()
  const { action: blockEditorAction, slateId: blockEditorSlateId } =
    useCustomizerBlockSettings()
  const slateKitProps = useSlateKitContext()
  const queryParams = useQuery()
  const urlParams = useParams()
  const { data: network } = useNetwork()
  const { data: actor } = useAuthMember()

  const {
    kit: { userStorage },
  } = useSlateKitContext()
  const getCachedExpandedItems = (id: string) =>
    userStorage.getItem<TreeItemIndex[]>(`community_editor.${id}.expandedItems`)
  const setCachedExpandedItems = (id: string, items: TreeItemIndex[]) =>
    userStorage.setItem(`community_editor.${id}.expandedItems`, items)

  const [expandedItems, setExpandedItems] = useState<
    Record<string, TreeItemIndex[]>
  >(
    slates.reduce(
      (preValue, slate) => ({
        ...preValue,
        [slate.id]: getCachedExpandedItems(slate.id) || [
          `${slate.id}::${slate.slate.rootBlock}`,
        ],
      }),
      {},
    ),
  )

  const slatesProps: Record<string, ExtendedSlateProps> =
    getExtendedSlatesProps({
      slates,
      slateKitProps,
      state,
      mode: 'edit',
      dispatch,
      expandedItems,
      setExpandedItems,
      setCachedExpandedItems,
      queryParams,
      urlParams,
      path,
      context: {
        networkId: network?.id,
        actorId: actor?.id,
        collectionId: space?.collection?.id,
        spaceId,
        memberId: null,
        postTypeId: null,
        postId: null,
        appId: null,
        dynamicBlockKey: null,
        network,
        space,
      },
    })

  const slatesPropsKeys = Object.keys(slatesProps)
  const items: Record<string, SlateTreeItemInfo> = slatesPropsKeys.reduce(
    (slates, slateId) => ({
      ...slates,
      ...getSlateTreeItems({
        id: slateId,
        slate: slatesProps[slateId].compiledSlate,
      }),
    }),
    {},
  )

  const {
    setExpandItems,
    canDropAt,
    onCollapse,
    onFocus,
    onExpand,
    onRename,
    onDrop,
  } = getSlateTreeActions({
    items,
    setActiveTab: (id, tab) =>
      dispatch({
        type: CustomizerActionType.SET_ACTIVE_TAB,
        payload: {
          id,
          tab,
        },
      }),
    slatesProps,
  })

  useEffect(() => {
    if (selectedBlock) {
      setExpandItems({ itemId: selectedBlock, includeItem: false })
    }
  }, [selectedBlock])

  if (typeof window === 'undefined') {
    return null
  }
  return (
    <SlateEditorContext.Provider value={slatesProps}>
      <ControlledTreeEnvironment
        items={items}
        getItemTitle={item => item.data.config.displayName}
        viewState={slatesPropsKeys.reduce(
          (preValue, slateId) => ({
            ...preValue,
            [slateId]: {
              focusedItem: state.selectedBlock || slateId,
              expandedItems: slatesProps[slateId].expandedItems,
            },
          }),
          {},
        )}
        defaultInteractionMode={{
          mode: 'custom',
          extends: InteractionMode.ClickArrowToExpand,
          createInteractiveElementProps: () => ({
            onDoubleClick: () => {
              // The double click is handled in SlateTreeItem.tsx
              // We should keep this empty to avoid the default click behavior
            },
          }),
        }}
        keyboardBindings={{
          renameItem: ['ENTER'],
        }}
        canRename
        canDragAndDrop
        canDropOnFolder
        canDropOnNonFolder={false}
        canReorderItems
        canDropAt={(items, target) =>
          items.every(item => canDropAt(item, target))
        }
        onFocusItem={onFocus}
        onExpandItem={onExpand}
        onCollapseItem={onCollapse}
        onRenameItem={onRename}
        onDrop={(items, target) => items.forEach(item => onDrop(item, target))}
        renderItemArrow={({ item, context }) =>
          item.isFolder ? (
            <div
              className={clsx(
                'cursor-pointer p-1 rounded-full hover:bg-surface-hovered flex-shrink-0 transform transition-transform',
                !context.isExpanded && '-rotate-90 rtl:rotate-90',
              )}
              {...context.arrowProps}
            >
              <SvgIcon className="w-4 h-4" name="chevron-down" />
            </div>
          ) : (
            <div className="w-6 h-6"></div>
          )
        }
        renderItem={SlateTreeItem}
        renderRenameInput={({ inputProps, inputRef, formProps }) => {
          return (
            <form {...formProps}>
              <input {...inputProps} ref={inputRef} className="w-full px-1" />
            </form>
          )
        }}
        renderDraggingItem={() => <div />}
        renderDraggingItemTitle={() => <div />}
        renderTreeContainer={({ children, containerProps }) => (
          <div>
            <div {...containerProps}>{children}</div>
          </div>
        )}
        renderItemsContainer={({ children, containerProps }) => (
          <ul {...containerProps} className="flex flex-col">
            {children}
          </ul>
        )}
        renderDragBetweenLine={({ draggingPosition, lineProps }) => (
          <div
            {...lineProps}
            style={{
              left: `${(draggingPosition.depth - 1) * renderDepthOffset}px`,
            }}
            className={clsx(
              'absolute end-0 -top-0.5 h-1 bg-action-primary',
              draggingPosition.targetType === 'between-items' &&
                draggingPosition.linePosition === 'top' &&
                'top-0',
              draggingPosition.targetType === 'between-items' &&
                draggingPosition.linePosition === 'bottom' &&
                '-top-1',
            )}
          />
        )}
        renderSearchInput={({ inputProps }) => (
          <div className={clsx('absolute top-0 end-0')}>
            <input {...inputProps} className={clsx('absolute w-32')} />
          </div>
        )}
        renderLiveDescriptorContainer={({ tree, children }) => (
          <div
            id={`rct-livedescription-${tree.treeId}`}
            style={{
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              height: '1px',
              overflow: 'hidden',
              position: 'absolute',
              whiteSpace: 'nowrap',
              width: '1px',
            }}
          >
            {children}
          </div>
        )}
        canSearch={false}
      >
        <div className="space-y-2">
          {!blockEditorAction && !editNavigation && (
            <LayoutPicker spaceId={space.id} currentLayout={layout} />
          )}
          {!blockEditorAction && editNavigation && <DisableNavigationPicker />}
          {!blockEditorAction && children}
          {blockEditorAction && (
            <BlockEditor slateProps={slatesProps[blockEditorSlateId]} />
          )}
        </div>
      </ControlledTreeEnvironment>
    </SlateEditorContext.Provider>
  )
}
